import { integration_path, integrations_calendars_google_path } from "@/routes";
import { ProviderAccount } from "@/types/serializers";
import { router } from "@inertiajs/react";
import { useState } from "react";
import { Box, Flex, Text, Button, VStack } from "@chakra-ui/react";
import { Checkbox } from "@/components/ui/checkbox";

type AccountCardProps = {
  providerAccount: ProviderAccount;
  max: number;
};

const AccountCard = ({ providerAccount, max }: AccountCardProps) => {
  const calendars = providerAccount.calendars || [];
  const [saving, setSaving] = useState<boolean>(false);

  // Track enabled calendar IDs explicitly for controlled checkboxes
  const [enabledIds, setEnabledIds] = useState<Set<number>>(
    () => new Set(calendars.filter((c) => c.enabled).map((c) => c.id)),
  );

  const selectedCount = enabledIds.size;
  const reachedLimit = selectedCount >= max;

  const handleToggle = (calendarId: number, checked: boolean) => {
    setEnabledIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(calendarId);
      } else {
        next.delete(calendarId);
      }
      return next;
    });
  };

  const save = (e: React.FormEvent) => {
    e.preventDefault();

    setSaving(true);
    const ids = Array.from(enabledIds);

    router.patch(
      integration_path(providerAccount.id),
      { integrations: { enabled_calendar_ids: ids } },
      { preserveScroll: true, onFinish: () => setSaving(false) },
    );
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={4}>
      <Flex alignItems="center" justifyContent="space-between">
        <Box fontSize="sm" color="gray.800">
          <Text fontWeight="medium">{providerAccount.email || "Google account"}</Text>
          <Text fontSize="xs" color="gray.500">
            UID: {providerAccount.uid}
          </Text>
          {providerAccount.auth_invalid && (
            <Box mt={2} p={2} bg="red.50" borderRadius="md" borderWidth="1px" borderColor="red.200">
              <Text fontSize="xs" color="red.700" fontWeight="medium">
                ⚠️ Authentication expired
              </Text>
              <Text fontSize="xs" color="red.600" mt={1}>
                Please reconnect your Google account to resume syncing.
              </Text>
              <Button
                size="xs"
                mt={2}
                colorPalette="red"
                variant="solid"
                onClick={() => router.post(integrations_calendars_google_path(providerAccount.id))}
              >
                Reconnect
              </Button>
            </Box>
          )}
        </Box>
        <Button
          size="sm"
          colorPalette="red"
          variant="subtle"
          onClick={() => router.delete(integrations_calendars_google_path(providerAccount.id))}
        >
          Disconnect
        </Button>
      </Flex>

      <Box mt={3}>
        <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={2}>
          Calendars
        </Text>
        <form onSubmit={save}>
          {reachedLimit && (
            <Box mb={2} p={2} bg="orange.50" borderRadius="md" borderWidth="1px" borderColor="orange.200">
              <Text fontSize="xs" color="orange.700">
                Maximum calendars selected ({max}/{max})
              </Text>
            </Box>
          )}
          <VStack gap={1} alignItems="flex-start">
            {calendars.map((calendar) => {
              const isChecked = enabledIds.has(calendar.id);
              return (
                <Checkbox
                  key={calendar.id}
                  checked={isChecked}
                  disabled={saving || (!isChecked && reachedLimit)}
                  colorPalette="primary"
                  onCheckedChange={(e) => handleToggle(calendar.id, !!e.checked)}
                >
                  {calendar.summary}
                </Checkbox>
              );
            })}
          </VStack>
          <Flex pt={2} alignItems="center" gap={3}>
            <Button type="submit" size="sm" bg="gray.900" color="white" _hover={{ bg: "black" }} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
            <Text fontSize="xs" color="gray.500">
              {selectedCount}/{max} selected
            </Text>
          </Flex>
        </form>
      </Box>
    </Box>
  );
};

export default AccountCard;
